import React from 'react';

import { Heading } from '@depop/web-thrift-ui/Typography';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import {
  ActivityTrackerEventType,
  moduleOriginParamName,
  NavigationComponentTypes,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { TrackingType } from '@/modules/home/types.ts';
import { type ImageLinkImage } from '@/modules/meganav/types';
import { appendParams } from '@/modules/routing/appendParams/appendParams.ts';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type FeaturedImageProps = {
  label: string;
  imageDesktop: ImageLinkImage;
  imageMobile: ImageLinkImage;
  slug: string | null;
  hideLabel?: boolean | null;
};

export function FeaturedImage({
  label,
  imageDesktop,
  imageMobile,
  slug,
  hideLabel = false,
}: FeaturedImageProps) {
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  function sendTracking({ href, label }: { href: string; label: string }) {
    sendActivityTrackerEvent(ActivityTrackerEventType.NAVBAR_CLICK_ACTION, {
      href,
      label: label || '',
      componentType: NavigationComponentTypes.ImageLink,
      schemaVersion: '1.0',
    });
  }

  const path = slug || '';

  if (!imageDesktop?.url && !imageMobile?.url) {
    return null;
  }

  return (
    <div className={styles.featuredImageContainer}>
      <ExtendedLink
        onClick={() => sendTracking({ href: path, label })}
        href={appendParams(path, [moduleOriginParamName, TrackingType.meganav])}
      >
        {imageDesktop?.url && (
          <div className={styles.desktopImage}>
            <LazyLoadImage
              alt={imageDesktop?.description || label}
              src={imageDesktop.url}
            />
          </div>
        )}
        {imageMobile?.url && (
          <div className={styles.mobileImage}>
            <LazyLoadImage
              alt={imageMobile?.description || label}
              src={imageMobile.url}
            />
          </div>
        )}
        {!hideLabel && (
          <Heading variant="sm" className={styles.descriptionText}>
            {label}
          </Heading>
        )}
      </ExtendedLink>
    </div>
  );
}
